'use client'

import {useEffect, useRef, useState} from 'react'
import Script from 'next/script'
import {useCookieConsentStore} from '@local/stores/stores'
import {Button, Locale, Slideout, SmallText, Toggle} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {GoogleAnalytics} from '@next/third-parties/google'

import CloseSVG from '@local/assets/svgs/icon-close.svg'
import {acceptAllConsent, DEFAULT_CONSENT, updateConsent, initConsent} from '@local/utils/gtm'

interface CookieFirstBannerProps {
	title: string
	content: string
	ctaCustomizeLabel: string
	ctaAcceptAllLabel: string
	modalTitle: string
	ctaModalSaveLabel: string
	ctaModalAcceptAllLabel: string
	lead?: string
	cookiesUrl: string
	lang: string
	categories
	usedCookies: {
		type: string
		domain: string
		provider: string
		expiration: string
	},
	preconsent: {
		advertising: boolean
		functional: boolean
		necessary: boolean
		performance: boolean
	}
}

declare global {
	interface Window {
		CookieFirst
	}
}

export default ({title, content, ctaCustomizeLabel, ctaAcceptAllLabel, children, modalTitle, ctaModalSaveLabel, ctaModalAcceptAllLabel, preconsent, lead, categories}: CookieFirstBannerProps) => {
	const [bannerHidden, setBannerHidden] = useState(true)
	const [canLoadGtm, setCanLoadGtm] = useState(false)
	const slideoutRef = useRef<HTMLElement>()
	const checkboxesRef = useRef<HTMLInputElement[]>([])
	const [isPanelOpened, setIsPanelOpened] = useCookieConsentStore(state => [state.isPanelOpened, state.setIsPanelOpened])

	const loadGtm = (consent) => {
		const {performance, functional, advertising} = consent
		if (performance || functional || advertising) {
			// load GTM only if necessary
			setCanLoadGtm(true)
		}
	}

	const handleClickCustomize = async () => {
		setIsPanelOpened(true)
	}

	const handleClickAccept = () => {
		acceptAllConsent()
		setBannerHidden(true)
		setCanLoadGtm(true)
		setIsPanelOpened(false)
	}

	const handleSubmit = (event) => {
		event.preventDefault()

		const consent = {...DEFAULT_CONSENT}

		if (checkboxesRef.current) {
			checkboxesRef.current.forEach(cb => {
				consent[(cb as HTMLInputElement).name] = (cb as HTMLInputElement).checked
			})
			consent.necessary = true
			updateConsent(consent)
			loadGtm(consent)
		}

		setBannerHidden(true)
		setIsPanelOpened(false)
	}

	useEffect(() => {
		const {consent, restored} = initConsent(preconsent)

		if (restored) {
			loadGtm(consent)

			checkboxesRef.current.forEach(cb => {
				(cb as HTMLInputElement).checked = consent[(cb as HTMLInputElement).name]
			})
		} else {
			// display cookie banner
			setBannerHidden(false)
		}
	}, [setCanLoadGtm])

	return (
		<>

			<Script id='gtm-datalayer'>
				{`
window.dataLayer = window.dataLayer || [];
function gtag(){window.dataLayer.push(arguments);}
				`}
			</Script>

			{canLoadGtm && (
				<Script>
					{`
					(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
					new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
					j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
					'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
					})(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM}');
					`}
				</Script>
			)}
			{canLoadGtm && <GoogleAnalytics gaId={process.env.NEXT_PUBLIC_GA || ''} />}

			<aside
				className={cx(
					'fixed z-100 right-0 left-0 bottom-0 lg:margin-p-1 cursor-none',
					'transition-[transform,visibility] duration-500',
					bannerHidden ? 'translate-y-full invisible ease-quart-inout' : 'ease-expo-out',
				)}
				aria-label={title}>
				<div className='flex lg:rounded-sm flex-col lg:flex-row items-center lg:justify-between bg-grey-1 margin-px-1 py-16 lg:p-20 cursor-auto'>
					<div className='lg:span-w-6'>
						<SmallText className='text-left mb-16 lg:mb-12 self-start'><Locale>Cookie Notice</Locale></SmallText>
						<div className='text-14 font-normal tracking-tight [&_a]:underline text-black lg-max:mb-16' dangerouslySetInnerHTML={{__html: content}} />
					</div>
					<div className='lg:flex lg:flex-row lg:gutter-gap-1 w-full lg:span-w-3-wider'>
						<Button className='w-full !text-12 flex-1' small border variant='secondary-transparent' onClick={handleClickCustomize}>
							{ctaCustomizeLabel}
						</Button>
						<Button className='lg-max:mt-10 !text-12 w-full flex-1' small variant='primary' onClick={handleClickAccept}>
							{ctaAcceptAllLabel}
						</Button>
					</div>
				</div>
				<div className={cx(
					'fixed inset-0 -z-1 w-full h-full bg-black opacity-30 pointer-events-none',
					'transition-opacity duration-500',
					'lg:hidden',
					bannerHidden ? 'invisible ease-quart-inout' : 'ease-expo-out',
				)} />
			</aside>
			<Slideout
				ref={slideoutRef}
				variant='right'
				isOpen={isPanelOpened}
				as='form'
				className='text-white bg-grey-4'
				onClickClose={() => setIsPanelOpened(false)}
				childrenProps={{onSubmit: handleSubmit}}>
				<header className='sticky top-0 z-1 flex items-center justify-between w-full py-10 lg:py-20 bg-grey-4'>
					<h2 className='w-fit uppercase'>{modalTitle}</h2>
					<Button
						small
						variant='icon'
						onClick={() => setIsPanelOpened(false)}
						className='px-16 aspect-square'>
						<CloseSVG />
					</Button>
				</header>
				<main
					className={cx(
						'py-20 gap-10',
						'overflow-y-auto overscroll-contain scrollbar-hide h-full',
					)}
					data-lenis-prevent>
					{lead && <p className='text-12 mb-20 uppercase lg:span-w-3'>{lead}</p>}
					{categories?.map(([key, {title, excerpt}], i) => (
						key !== 'common' && (
							<section key={key} className='text-12 mb-20 text-grey-1'>
								<header className='flex items-center justify-between mb-5 pb-5 border-b border-grey-1/20'>
									<h3 className='text-11 text-white uppercase tracking-tight'>{title}</h3>
									<Toggle
										ref={el => { checkboxesRef.current[i - 1] = el }}
										name={key}
										theme='dark'
										defaultChecked={preconsent?.[key] || key === 'necessary'}
										disabled={key === 'necessary'} />
								</header>
								<p>{excerpt}</p>
								{/* <p>{description}</p> */}
							</section>
						)
					))}
					{children}
				</main>
				<footer className='flex-shrink-0 mt-auto'>
					<div className='flex flex-row gutter-gap-1 lg-max:margin-my-1 lg:my-20'>
						<Button className='text-11 px-0 w-full border-white' variant='primary-transparent' small border type='submit'>
							{ctaModalSaveLabel}
						</Button>
						<Button className='text-11 px-0 w-full' variant='primary' small type='button' onClick={handleClickAccept}>
							{ctaModalAcceptAllLabel}
						</Button>
					</div>
				</footer>
			</Slideout>
		</>
	)
}
